import React from "react";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { Typography, Button, Box } from "@mui/material";
import { useLocation } from "react-router-dom";
import { triggerHapticFeedback } from "../utils/mobileDetection";

const ConnectButtonCustom = () => {
  const handleButtonClick = (callback) => {
    // Trigger haptic feedback on mobile devices
    triggerHapticFeedback("light");
    callback();
  };

  return (
    <ConnectButton.Custom>
      {({
        account,
        chain,
        openAccountModal,
        openChainModal,
        openConnectModal,
        authenticationStatus,
        mounted,
      }) => {
        const ready = mounted && authenticationStatus !== "loading";
        const connected =
          ready &&
          account &&
          chain &&
          (!authenticationStatus || authenticationStatus === "authenticated");

        return (
          <div
            {...(!ready && {
              "aria-hidden": true,
              style: {
                opacity: 0,
                pointerEvents: "none",
                userSelect: "none",
              },
            })}
          >
            {(() => {
              if (!connected) {
                return (
                  <Button
                    onClick={() => handleButtonClick(openConnectModal)}
                    variant="outlined"
                    aria-label="Connect your cryptocurrency wallet to TrendPad"
                    sx={{
                      borderRadius: "17px",
                      border: "2px solid #2299b7",
                      textTransform: "none",
                      fontSize: { xs: "0.9rem", sm: "1rem", md: "1.25rem" },
                      padding: {
                        xs: "12px 16px",
                        sm: "10px 18px",
                        md: "10px 20px",
                      },
                      minHeight: { xs: "44px", sm: "auto" }, // iOS minimum touch target
                      color: "text.primary",
                      gap: 1,
                      width: "100%",
                      maxWidth: { xs: "100%", sm: "auto" },
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      "&:hover": {
                        border: "2px solid #2299b7",
                        background: "rgba(34, 153, 183, 0.1)",
                      },
                      "&:active": {
                        transform: "scale(0.98)", // Subtle press effect
                      },
                    }}
                  >
                    Connect Wallet
                    <img
                      src="/images/fluent_wallet-credit-card-16-regular.png"
                      alt="Wallet"
                      style={{ width: 20, height: 20 }}
                    />
                  </Button>
                );
              }

              if (chain.unsupported) {
                return (
                  <Button
                    onClick={() => handleButtonClick(openChainModal)}
                    variant="contained"
                    color="error"
                    sx={{
                      borderRadius: "17px",
                      textTransform: "none",
                      fontSize: { xs: "0.85rem", sm: "0.9rem", md: "1.1rem" },
                      padding: {
                        xs: "12px 16px",
                        sm: "10px 18px",
                        md: "10px 20px",
                      },
                      minHeight: { xs: "44px", sm: "auto" },
                      width: "100%",
                      maxWidth: { xs: "100%", sm: "auto" },
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      "&:active": {
                        transform: "scale(0.98)",
                      },
                    }}
                  >
                    Wrong Network
                  </Button>
                );
              }

              return (
                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: 0.5, sm: 1 },
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Button
                    onClick={() => handleButtonClick(openChainModal)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      background: "transparent",
                      border: "none",
                      boxShadow: "none",
                      color: "text.primary",
                      textTransform: "none",
                      minWidth: { xs: "44px", sm: "auto" },
                      minHeight: { xs: "44px", sm: "auto" },
                      padding: { xs: "10px", sm: "8px" },
                      "&:hover": { background: "rgba(255, 255, 255, 0.05)" },
                      "&:active": { transform: "scale(0.95)" },
                    }}
                  >
                    {chain.hasIcon && (
                      <Box
                        component="img"
                        src={chain.iconUrl}
                        alt={chain.name ?? "Chain icon"}
                        sx={{
                          width: { xs: 24, sm: 28 },
                          height: { xs: 24, sm: 28 },
                          borderRadius: 999,
                        }}
                      />
                    )}
                  </Button>

                  <Button
                    onClick={() => handleButtonClick(openAccountModal)}
                    variant="outlined"
                    aria-label={`View wallet details for ${account.displayName}`}
                    sx={{
                      borderRadius: "17px",
                      border: "2px solid #2299b7",
                      textTransform: "none",
                      fontSize: { xs: "0.85rem", sm: "1rem", md: "1.25rem" },
                      padding: {
                        xs: "10px 14px",
                        sm: "10px 18px",
                        md: "10px 20px",
                      },
                      minHeight: { xs: "44px", sm: "auto" },
                      color: "text.primary",
                      gap: { xs: 0.5, sm: 1 },
                      flex: 1,
                      maxWidth: { xs: "200px", sm: "auto" },
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      "&:hover": {
                        border: "2px solid #2299b7",
                        background: "rgba(34, 153, 183, 0.1)",
                      },
                      "&:active": {
                        transform: "scale(0.98)",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100%",
                      }}
                    >
                      {account.displayName}
                    </Box>
                    <img
                      src="/images/fluent_wallet-credit-card-16-regular.png"
                      alt="Wallet"
                      style={{ width: 18, height: 18, flexShrink: 0 }}
                    />
                  </Button>
                </Box>
              );
            })()}
          </div>
        );
      }}
    </ConnectButton.Custom>
  );
};

export default ConnectButtonCustom;
